<template>
  <div v-if="currentTicket && isStarted">
    <p>
      Multiplier: <strong>{{ multiplier.toFixed(2) }}x</strong>
    </p>
    <button type="button" :disabled="!canStop" @click.stop.prevent="stop">Stop (Cashout)</button>
  </div>
</template>

<script>
import {
  finalizeGameTicket,
  getCrashTicketByGuid,
} from "../api.js";
import crashHub from "../plugins/SignalRPlugin/CrashHubClient.js";
import { Parser } from "expr-eval";

export default {
  name: "CrashGameView",
  props: {
    chosenGame: {
      type: Object,
      required: true,
    },
    currentTicket: {
      type: Object,
      required: false,
      default: null,
    },
  },
  emits: ["round-state", "ticket-result"],
  data() {
    return {
      countdownSeconds: 0,
      countdownTimer: null,
      isStarted: false,
      multiplier: 1.0,
      stopMultiplier: null,
      multiplierTimer: null,
      subscribedGameId: null,
      resultEmitted: false,
      handlersRegistered: false,
    };
  },
  computed: {
    canStop() {
      return this.isStarted && this.multiplier > 1.0;
    },
  },
  watch: {
    chosenGame: {
      handler() {
        this.subscribeToChosenGame();
      },
      immediate: true,
    },
    currentTicket: {
      handler(newVal) {
        if (newVal) {
          this.stopMultiplier = null;
          this.resultEmitted = false;
        } else {
          this.clearTicketState();
        }
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    this.cleanup();
    this.unsubscribeFromCurrentGame();
  },
  methods: {
    isCorrectGame(gameId) {
      return Number(this.chosenGame?.pluginGameId) === Number(gameId);
    },
    getTicketId() {
      return this.currentTicket?.ticketIdentifier;
    },
    clearTimer(timer) {
      if (timer) {
        (timer === this.countdownTimer ? clearTimeout : clearInterval)(timer);
      }
      if (timer === this.countdownTimer) this.countdownTimer = null;
      if (timer === this.multiplierTimer) this.multiplierTimer = null;
    },
    emitRoundState(state, countdownSeconds = 0) {
      this.$emit("round-state", {
        state,
        countdownSeconds: Math.max(0, countdownSeconds),
      });
    },
    emitTicketResult(status, finalMultiplier, cashoutMultiplier = null) {
      if (this.resultEmitted) return;

      const ticketIdentifier = this.getTicketId();
      if (!ticketIdentifier) return;

      this.$emit("ticket-result", {
        ticketIdentifier,
        status,
        finalMultiplier,
        cashoutMultiplier,
      });

      this.resultEmitted = true;
    },
    async subscribeToChosenGame() {
      const gameId = Number(this.chosenGame?.pluginGameId) || 0;

      if (!gameId) {
        await this.unsubscribeFromCurrentGame();
        this.emitRoundState("idle", 0);
        return;
      }

      await crashHub.startConnection();
      this.registerHubHandlersOnce();

      if (this.subscribedGameId !== gameId) {
        if (this.subscribedGameId) await crashHub.unsubscribeFromGame(this.subscribedGameId);
        await crashHub.subscribeToGame(gameId);
        this.subscribedGameId = gameId;
      }
    },
    async unsubscribeFromCurrentGame() {
      if (!this.subscribedGameId) return;
      try {
        await crashHub.unsubscribeFromGame(this.subscribedGameId);
      } catch (err) {
        console.warn("unsubscribeFromGame failed", err);
      } finally {
        this.subscribedGameId = null;
      }
    },
    registerHubHandlersOnce() {
      if (this.handlersRegistered) return;
      this.handlersRegistered = true;

      const gameHandler = (callback) => (gameId, ...args) => {
        if (this.isCorrectGame(gameId)) callback(...args);
      };

      crashHub.on("GameStarted", gameHandler((start, expr) => this.handleGameStarted(start || 1.0, expr)));
      crashHub.on("GamePending", gameHandler((secs) => {
        this.isStarted = false;
        this.countdownSeconds = Math.max(0, secs || 0);
        this.emitRoundState("pending", this.countdownSeconds);
        this.startCountdown();
      }));
      crashHub.on("GameEnded", gameHandler((final) => {
        this.emitRoundState("idle", 0);
        this.handleGameEnded(final || this.multiplier);
        this.emitTicketResult(
          this.stopMultiplier ? "cashedout" : "crashed",
          final || this.multiplier,
          this.stopMultiplier,
        );
      }));
    },
    startCountdown() {
      if (this.countdownSeconds <= 0 || this.isStarted) return;
      
      this.clearTimer(this.countdownTimer);
      const tick = () => {
        if (this.isStarted) {
          this.clearTimer(this.countdownTimer);
          return;
        }
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
          this.emitRoundState("pending", this.countdownSeconds);
          this.countdownTimer = setTimeout(tick, 1000);
        } else {
          this.clearTimer(this.countdownTimer);
        }
      };
      this.countdownTimer = setTimeout(tick, 1000);
    },
    startMultiplierLoop(expr) {
      if (!expr?.trim?.()) {
        console.error("multiplierExpression missing or empty");
        this.isStarted = false;
        return;
      }

      let parsedExpr;
      try {
        parsedExpr = new Parser().parse(expr);
      } catch (err) {
        console.error("Failed to parse multiplier expression", err);
        this.isStarted = false;
        return;
      }

      const startTs = Date.now();
      this.multiplierTimer = setInterval(() => {
        if (!this.isStarted) return;
        try {
          this.multiplier = Number(parsedExpr.evaluate({ time: Date.now() - startTs })) || 0;
        } catch (err) {
          console.error("Multiplier loop error", err);
          this.isStarted = false;
          this.clearTimer(this.multiplierTimer);
        }
      }, 100);
    },
    async stop() {
      if (!this.currentTicket || !this.canStop) return;
      
      const ticketGuid = this.getTicketId();
      if (!ticketGuid) return;

      try {
        console.debug('CrashGameView.stop invoking hub stop', ticketGuid);
        if (!await crashHub.invokeStop(ticketGuid)) return;
        console.debug('CrashGameView.stop hub stop ok');
        
        this.isStarted = false;
        this.stopMultiplier = Math.round(this.multiplier * 100) / 100;
        this.clearTimer(this.multiplierTimer);

        await finalizeGameTicket('PUT', 'Crash', ticketGuid);
        const updated = await getCrashTicketByGuid(ticketGuid);

        const serverCashoutMultiplier =
          updated?.price > 0 && updated?.prize >= 0
            ? Math.round((updated.prize / updated.price) * 100) / 100
            : null;
        const resolvedMultiplier = serverCashoutMultiplier || this.stopMultiplier || this.multiplier;

        this.multiplier = resolvedMultiplier;
        this.emitTicketResult(
          "cashedout",
          resolvedMultiplier,
          resolvedMultiplier,
        );
      } catch (e) {
        console.error("stop failed", e);
      }
    },
    handleGameStarted(startingMultiplier, multiplierExpr) {
      this.isStarted = true;
      this.emitRoundState("started", 0);
      this.clearTimer(this.countdownTimer);
      this.countdownSeconds = 0;
      this.multiplier = startingMultiplier || 1.0;
      if (!this.multiplierTimer) this.startMultiplierLoop(multiplierExpr);
    },
    handleGameEnded(finalMultiplier) {
      this.isStarted = false;
      this.multiplier = finalMultiplier || this.multiplier;
      this.clearTimer(this.multiplierTimer);
    },
    cleanup() {
      this.clearTimer(this.countdownTimer);
      this.clearTimer(this.multiplierTimer);
    },
    clearTicketState() {
      this.stopMultiplier = null;
      this.resultEmitted = false;
      this.clearTimer(this.multiplierTimer);
    },
  },
};
</script>
